import ExcelJS from "exceljs";

/**
 * Descarga un blob como archivo (sin file-saver)
 */
function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Exporta datos a un archivo Excel usando exceljs
 * @param data Array de objetos a exportar
 * @param filename Nombre del archivo (sin extensión)
 * @param sheetName Nombre de la hoja (opcional)
 */
export async function exportToExcel<T extends Record<string, any>>(
  data: T[],
  filename: string,
  sheetName: string = "Datos",
): Promise<void> {
  if (!data || data.length === 0) {
    console.warn("No hay datos para exportar");
    return;
  }

  try {
    // Crear nuevo libro de trabajo
    const workbook = new ExcelJS.Workbook();
    workbook.creator = "QvaPOS";
    workbook.created = new Date();

    // Agregar hoja de trabajo
    const worksheet = workbook.addWorksheet(sheetName);

    // Obtener cabeceras desde las keys del primer objeto
    const headers = Object.keys(data[0]);

    // Agregar cabeceras con estilo
    worksheet.addRow(headers);

    // Estilo para cabeceras
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF2E86C1" }, // Azul
    };
    headerRow.alignment = { vertical: "middle", horizontal: "center" };

    // Agregar datos
    data.forEach((row) => {
      const rowData = headers.map((header) => row[header]);
      worksheet.addRow(rowData);
    });

    // Ajustar ancho de columnas automáticamente
    worksheet.columns = headers.map((header) => ({
      header,
      key: header,
      width:
        Math.max(
          header.length,
          ...data.map((row) => String(row[header] || "").length),
        ) + 2, // Un poco de padding
    }));

    // Estilo para filas de datos (alternar colores)
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber > 1) {
        // Saltar cabecera
        row.alignment = { vertical: "middle", horizontal: "left" };
        if (rowNumber % 2 === 0) {
          row.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFF2F2F2" }, // Gris claro
          };
        }
      }
    });

    // Generar archivo Excel
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    downloadBlob(blob, `${filename}.xlsx`);
  } catch (error) {
    console.error("❌ Error exportando a Excel:", error);
    throw error;
  }
}

/**
 * Exporta ventas a Excel con formato específico
 */
export async function exportSalesToExcel(
  sales: any[],
  dateRange: string,
): Promise<void> {
  const formattedData = sales.map((sale) => {
    // Manejar fecha: usar fechaHora si existe (string formateado), sino parsear createdAt
    let fechaHora = "N/A";
    if (sale.fechaHora) {
      fechaHora = sale.fechaHora; // Ya viene formateado desde backend: "20/04/2026 02:28"
    } else if (sale.createdAt) {
      try {
        const dateObj = new Date(sale.createdAt);
        if (!isNaN(dateObj.getTime())) {
          fechaHora = dateObj.toLocaleString("es-CU", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          });
        }
      } catch (e) {
        console.warn("Error formateando fecha:", e);
      }
    }

    return {
      "Fecha y Hora": fechaHora,
      "ID Venta": sale.id,
      Productos: sale.products || "N/A",
      "Método de Pago": sale.paymentMethod,
      "Total (CUP)": Number(sale.total).toFixed(2),
      "Cantidad Ítems": sale.itemsCount || 0,
    };
  });

  await exportToExcel(
    formattedData,
    `ventas_${dateRange}_${new Date().toISOString().split("T")[0]}`,
    "Ventas",
  );
}

/**
 * Exporta productos vendidos a Excel
 */
export async function exportProductsToExcel(
  products: any[],
  viewType: string,
): Promise<void> {
  const formattedData = products.map((product, index) => ({
    "#": index + 1,
    Producto: product.productName,
    Categoria: product.category,
    "Unidades Vendidas": Number(product.totalQuantity),
    "Ingresos (CUP)": Number(product.totalRevenue).toFixed(2),
    Ventas: product.saleCount,
    "Promedio por Venta (CUP)": (
      Number(product.totalRevenue) / product.saleCount
    ).toFixed(2),
  }));

  const viewLabel = viewType === "quantity" ? "mas_vendidos" : "mas_rentables";
  await exportToExcel(
    formattedData,
    `productos_${viewLabel}_${new Date().toISOString().split("T")[0]}`,
    "Productos",
  );
}

/**
 * Exporta clientes a Excel
 */
export async function exportCustomersToExcel(
  customers: any[],
  viewType: string,
): Promise<void> {
  const formattedData = customers.map((customer, index) => ({
    "#": index + 1,
    Nombre: customer.fullName,
    CI: customer.ci,
    Teléfono: customer.phone,
    Email: customer.email || "",
    "Compras Totales": customer.totalPurchases,
    "Total Gastado (CUP)": customer.totalSpent.toFixed(2),
    "Promedio por Compra (CUP)": customer.averagePurchase.toFixed(2),
    "Última Compra": customer.lastPurchaseAt
      ? new Date(customer.lastPurchaseAt).toLocaleDateString()
      : "Nunca",
    "Días desde Última Compra": customer.daysSinceLastPurchase,
    Estado:
      customer.daysSinceLastPurchase <= 7
        ? "Activo"
        : customer.daysSinceLastPurchase <= 30
          ? "Regular"
          : "Inactivo",
  }));

  let filename = "clientes";
  if (viewType === "spent") filename = "clientes_mas_valiosos";
  if (viewType === "frequent") filename = "clientes_mas_frecuentes";
  if (viewType === "inactive") filename = "clientes_inactivos";

  await exportToExcel(
    formattedData,
    `${filename}_${new Date().toISOString().split("T")[0]}`,
    "Clientes",
  );
}

/**
 * Exporta transferencias a Excel con formato específico
 */
export async function exportTransfersToExcel(
  transfers: any[],
  dateRange: string,
): Promise<void> {
  const formattedData = transfers.map((t) => {
    // Formatear fecha
    let fechaHora = "N/A";
    if (t.createdAt) {
      try {
        const dateObj = new Date(t.createdAt);
        if (!isNaN(dateObj.getTime())) {
          fechaHora = dateObj.toLocaleString("es-CU", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          });
        }
      } catch (e) {
        console.warn("Error formateando fecha:", e);
      }
    }

    return {
      "Fecha y Hora": fechaHora,
      "ID Venta": t.id,
      Banco: t.bank || "N/A",
      "Código Transferencia": t.transferCode || "N/A",
      "Tarjeta Destino": t.destinationCard || "N/A", // <--- COLUMNA NUEVA
      Cliente: t.fullName || t.customerName || "N/A",
      CI: t.ci || t.customerCi || "N/A",
      Teléfono: t.phone || t.customerPhone || "N/A",
      "Monto (CUP)": Number(t.total).toFixed(2),
    };
  });

  await exportToExcel(
    formattedData,
    `transferencias_${dateRange}_${new Date().toISOString().split("T")[0]}`,
    "Transferencias",
  );
}
